// Identical transport in landing and panel. The server owns the visitor identity.
export function createTrackingClient(origin, transport = fetch) {
  let analytics = null;
  let preferences = null;
  let queue = Promise.resolve();
  const api = async (path, body) => {
    const response = await transport(`${origin}/tracking/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: AbortSignal.timeout(4000)
    });
    if (!response.ok) throw new Error('Tracking unavailable');
    return response.json();
  };
  const serialize = action => {
    const next = queue.catch(() => {}).then(action);
    queue = next;
    return next;
  };
  return {
    get analytics() {
      return analytics;
    },
    get preferences() {
      return preferences;
    },
    idle: () => queue.catch(() => {}),
    bootstrap: previous =>
      serialize(async () => {
        const current = await api('consent');
        analytics = current.analytics;
        preferences = current.preferences;
        if (analytics === null && typeof previous === 'boolean') {
          const saved = await api('consent', { analytics: previous });
          analytics = saved.analytics;
          preferences = saved.preferences;
        }
        return analytics;
      }),
    consent: value =>
      serialize(async () => {
        const saved = await api(
          'consent',
          typeof value === 'boolean'
            ? { analytics: value }
            : { preferences: value }
        );
        analytics = saved.analytics;
        preferences = saved.preferences;
        return analytics;
      }),
    visit: (path, ref, eventId) =>
      serialize(async () => {
        if (analytics !== true) return;
        // Never send queries, fragments or decoded identifiers from authenticated routes.
        const clean = path
          .split(/[?#]/)[0]
          .split('/')
          .map(p => (/[%@]/.test(p) ? ':id' : p))
          .join('/');
        const body = {
          path: clean,
          ref: /^[a-z0-9]{8,32}$/i.test(ref || '') ? ref : null,
          event_id: eventId
        };
        try {
          await api('visits', body);
        } catch {
          await api('visits', body);
        } // Same event ID makes a retry idempotent.
      })
  };
}
